import { useEffect, useMemo, useState } from "react";
import { api, CATEGORY_COLORS } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
    Plus,
    Pencil,
    Trash2,
    Building2,
    Users,
    ImageIcon,
    ChevronDown,
    ChevronUp,
    Award,
    Phone,
} from "lucide-react";
import { toast } from "sonner";
import OrganizationDialog from "@/components/OrganizationDialog";

const initials = (p) =>
    `${p.first_name?.[0] || ""}${p.last_name?.[0] || ""}`.toUpperCase() || "?";

export default function Organizations() {
    const [orgs, setOrgs] = useState([]);
    const [persons, setPersons] = useState([]);
    const [search, setSearch] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [viewingImage, setViewingImage] = useState(null);

    const load = async () => {
        const [o, p] = await Promise.all([
            api.get("/organizations"),
            api.get("/persons"),
        ]);
        setOrgs(o.data);
        setPersons(p.data);
    };

    useEffect(() => {
        load();
    }, []);

    const filtered = orgs.filter((o) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return `${o.name} ${o.description || ""}`.toLowerCase().includes(q);
    });

    const membersByOrg = useMemo(() => {
        const map = {};
        for (const p of persons) {
            if (!p.organization) continue;
            (map[p.organization] ||= []).push(p);
        }
        for (const k of Object.keys(map)) {
            map[k].sort((a, b) =>
                (a.rank || "").localeCompare(b.rank || "") ||
                (a.last_name || "").localeCompare(b.last_name || "")
            );
        }
        return map;
    }, [persons]);

    const remove = async (o) => {
        if (!window.confirm(`Supprimer l'organisation "${o.name}" ?`)) return;
        try {
            await api.delete(`/organizations/${o.id}`);
            toast.success("Organisation supprimée");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
    };

    return (
        <>
            <PageHeader
                title="Organisations"
                subtitle="Fiches de groupes avec photos, positions et membres"
                actions={
                    <Button
                        data-testid="org-add-btn"
                        onClick={() => {
                            setEditing(null);
                            setDialogOpen(true);
                        }}
                        className="bg-[#3D4E2E] hover:bg-[#2E3A22] text-white"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle organisation
                    </Button>
                }
            />

            <div className="p-8 space-y-6">
                <Input
                    data-testid="orgs-search-input"
                    placeholder="Rechercher une organisation…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="max-w-sm h-11 bg-white"
                />

                {filtered.length === 0 ? (
                    <div className="text-center text-slate-500 py-16 border border-dashed border-slate-200 rounded-lg bg-white">
                        Aucune organisation. Ajoutez-en une pour rassembler vos contacts.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {filtered.map((o) => {
                            const colorCls = CATEGORY_COLORS[o.color] || CATEGORY_COLORS.slate;
                            const members = membersByOrg[o.name] || [];
                            const isOpen = !!expanded[o.id];
                            return (
                                <Card
                                    key={o.id}
                                    data-testid={`org-card-${o.id}`}
                                    className="border-slate-200 shadow-sm bg-white overflow-hidden"
                                >
                                    <div className="p-5">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className={`w-11 h-11 rounded-md flex items-center justify-center border ${colorCls}`}>
                                                    <Building2 className="w-5 h-5" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="font-display text-lg font-semibold text-slate-900 truncate">
                                                        {o.name}
                                                    </div>
                                                    {o.description && (
                                                        <div className="text-sm text-slate-500 truncate">
                                                            {o.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                <Button size="sm" variant="ghost" data-testid={`org-edit-btn-${o.id}`} onClick={() => { setEditing(o); setDialogOpen(true); }}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button size="sm" variant="ghost" data-testid={`org-delete-btn-${o.id}`} onClick={() => remove(o)} className="text-slate-500 hover:text-rose-700 hover:bg-rose-50">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="mt-3 flex items-center gap-3 text-xs text-slate-600">
                                            <span className="inline-flex items-center gap-1">
                                                <Users className="w-3.5 h-3.5" /> {members.length} membre{members.length > 1 ? "s" : ""}
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <ImageIcon className="w-3.5 h-3.5" /> {o.images?.length || 0} image{(o.images?.length || 0) > 1 ? "s" : ""}
                                            </span>
                                        </div>

                                        {o.images?.length > 0 && (
                                            <div className="mt-4 grid grid-cols-3 gap-2">
                                                {o.images.slice(0, 6).map((img, i) => {
                                                    const src = typeof img === "string" ? img : img.url;
                                                    const caption = typeof img === "string" ? "" : img.caption;
                                                    return (
                                                        <button
                                                            key={i}
                                                            type="button"
                                                            onClick={() => setViewingImage({ src, caption })}
                                                            data-testid={`org-image-${o.id}-${i}`}
                                                            className="relative aspect-square rounded-md overflow-hidden bg-slate-100 hover:opacity-90 cursor-zoom-in"
                                                        >
                                                            <img src={src} alt={caption} className="w-full h-full object-cover" />
                                                            {caption && (
                                                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent text-white text-[11px] font-medium px-2 py-1 truncate">
                                                                    {caption}
                                                                </div>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {o.notes && (
                                            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 whitespace-pre-wrap">
                                                {o.notes}
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => setExpanded((s) => ({ ...s, [o.id]: !s[o.id] }))}
                                        data-testid={`org-toggle-members-${o.id}`}
                                        className="w-full px-5 py-3 border-t border-slate-100 flex items-center justify-between text-sm text-slate-700 hover:bg-slate-50"
                                    >
                                        <span className="font-medium">Membres attachés</span>
                                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                    </button>

                                    {isOpen && (
                                        <div className="px-5 pb-5 border-t border-slate-100 bg-slate-50 space-y-2 pt-3">
                                            {members.length === 0 ? (
                                                <div className="text-xs text-slate-500 py-3 text-center">
                                                    Aucun membre. Assignez cette organisation à des fiches Personnes.
                                                </div>
                                            ) : (
                                                members.map((p) => (
                                                    <div key={p.id} data-testid={`org-member-${p.id}`} className="flex items-center gap-3 bg-white rounded-md p-2 border border-slate-100">
                                                        <div className={`w-9 h-9 rounded-full overflow-hidden bg-[#DDE5D0] text-[#3D4E2E] flex items-center justify-center shrink-0 border border-[#3D4E2E]/20 ${p.deceased ? "grayscale" : ""}`}>
                                                            {p.photo_url ? (
                                                                <img src={p.photo_url} alt="" className="w-full h-full object-cover" />
                                                            ) : (
                                                                <span className="text-xs font-semibold">{initials(p)}</span>
                                                            )}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                                                                {p.deceased && <span className="w-4 h-4 inline-flex items-center justify-center rounded-full bg-slate-900 text-white text-[10px]">†</span>}
                                                                <span className={p.deceased ? "line-through text-slate-600" : ""}>{p.first_name} {p.last_name}</span>
                                                            </div>
                                                            <div className="text-xs text-slate-500 flex items-center gap-2">
                                                                {p.rank && <span className="inline-flex items-center gap-1"><Award className="w-3 h-3" />{p.rank}</span>}
                                                                {p.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>

            <OrganizationDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onDone={load} />

            <Dialog open={!!viewingImage} onOpenChange={(v) => !v && setViewingImage(null)}>
                <DialogContent className="sm:max-w-3xl p-0 overflow-hidden bg-slate-950 border-slate-800">
                    {viewingImage && (
                        <>
                            <img src={viewingImage.src} alt={viewingImage.caption || ""} className="w-full h-auto max-h-[85vh] object-contain bg-slate-950" />
                            {viewingImage.caption && (
                                <div className="px-6 py-3 bg-slate-900 text-slate-100 border-t border-slate-800 font-medium">
                                    {viewingImage.caption}
                                </div>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
